import { FormEvent, useState } from "react";
import { Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { z } from "zod";

import api, { setToken } from "../../utils/api/axios";
import { notifyApiError, notifyApiSuccess } from "../../utils/api/notifyApi";
import { UserLogin } from "../../types/api/user";
import { useAppDispatch } from "../../store/hook";
import { setAuthUser } from "../../store/reducers/auth.slice";
import ResetPasswordDialog from "./ResetPasswordDialog";

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isTestingIp, setIsTestingIp] = useState(false);

  const handleTestIpConnection = async () => {
    setIsTestingIp(true);
    try {
      const response = await api.get<{ success: boolean; message: string }>(
        "/auth/ip/test"
      );
      if (response.data.success) {
        notifyApiSuccess(response.data.message);
      } else {
        notifyApiError(response.data.message);
      }
    } catch (error) {
      notifyApiError("Erreur lors du test de connexion IP");
    } finally {
      setIsTestingIp(false);
    }
  };

  const { Field, Subscribe, handleSubmit } = useForm({
    validatorAdapter: zodValidator,
    onSubmit: async ({ value }) => {
      try {
        const userInfos = await api.post<UserLogin>("/auth/login", value);
        notifyApiSuccess("Connexion réussie");
        dispatch(setAuthUser(userInfos.data));
        setToken(userInfos.data.token.token);
        navigate("/");
      } catch (_) {
        return { errors: "Email ou mot de passe incorrect" };
      }
    },
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit();
  };

  return (
    <form className="form" onSubmit={(e) => submit(e)}>
      <div className="input-container">
        <Field
          name="email"
          defaultValue=""
          validators={{
            onChange: z.string().email("Renseignez un email valide"),
          }}
          children={(field) => (
            <>
              <p className="input-label">Email*</p>
              <TextField
                type="text"
                placeholder="Votre email"
                autoComplete="email"
                fullWidth
                value={field.state.value}
                error={!!field.state.meta.errors.length}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.errors.length ? (
                <em className="input-error">{field.state.meta.errors}</em>
              ) : null}
            </>
          )}
        />
      </div>
      <div className="input-container">
        <Field
          name="password"
          defaultValue=""
          validators={{
            onChange: z
              .string()
              .min(6, "Le mot de passe doit contenir au moins 6 caractères"),
          }}
          children={(field) => (
            <>
              <p className="input-label">Mot de passe*</p>
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                autoComplete="current-password"
                fullWidth
                value={field.state.value}
                error={!!field.state.meta.errors.length}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {field.state.meta.errors.length ? (
                <em className="input-error">{field.state.meta.errors}</em>
              ) : null}
            </>
          )}
        />
      </div>

      <Button
        onClick={() => setOpenResetPasswordDialog(true)}
        className="forgot-password"
      >
        Mot de passe oublié ?
      </Button>

      <Button
        onClick={handleTestIpConnection}
        disabled={isTestingIp}
        variant="text"
        sx={{
          color: "var(--navy)",
          textDecoration: "underline",
          "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
        }}
      >
        {isTestingIp ? "Test en cours..." : "Tester ma connexion par IP"}
      </Button>

      <Subscribe
        // @ts-ignore ignore build error
        selector={(state) => [state.canSubmit, state.isSubmitting]}
        // @ts-ignore ignore build error
        children={([canSubmit]) => (
          <Button
            sx={{
              backgroundColor: "#000",
              color: "white",
              "&:hover": {
                backgroundColor: "#000",
              },
            }}
            variant="contained"
            disabled={!canSubmit}
            type="submit"
            size="large"
          >
            Connexion
          </Button>
        )}
      />

      <ResetPasswordDialog
        open={openResetPasswordDialog}
        onClose={() => setOpenResetPasswordDialog(false)}
      />
    </form>
  );
};

export default Login;
